import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Power, PowerOff, Palette, Users, LifeBuoy, Trash2, UserPlus } from "lucide-react";

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="rounded-xl bg-slate-900/70 ring-1 ring-white/5 p-5 mb-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4 text-slate-200 font-semibold">
        {Icon && <Icon className="w-4 h-4 text-emerald-400" />} {title}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export default function PlatformCompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("");
  const [modules, setModules] = useState([]);
  const [branding, setBranding] = useState({});
  const [newMember, setNewMember] = useState({ email: "", role: "operations" });
  const [support, setSupport] = useState({ acting_role: "operations", mode: "read", reason: "", ticket_ref: "", minutes: 30 });

  const load = useCallback(() => {
    api.get(`/platform/companies/${id}`).then((r) => {
      setCompany(r.data.company);
      setMembers(r.data.members);
      setName(r.data.company.name);
      setPlan(r.data.company.plan);
      setModules(r.data.company.enabled_modules || []);
      setBranding(r.data.company.branding || {});
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);

  useEffect(() => {
    api.get("/platform/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
  }, [load]);

  if (!company || !meta) return <div className="p-8 text-slate-400">Loading company…</div>;

  const toggleModule = (m) => setModules((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));

  const saveCompany = async (extra = {}) => {
    try {
      await api.patch(`/platform/companies/${id}`, { name, plan, enabled_modules: modules, ...extra });
      toast.success("Company updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const applyPlanBundle = () => {
    if (window.confirm(`Reset modules to the ${plan} plan bundle? This overwrites any custom module selection.`)) {
      saveCompany({ confirm_module_reset: true, enabled_modules: undefined });
    }
  };

  const setStatus = async (action) => {
    try {
      await api.post(`/platform/companies/${id}/${action}`);
      toast.success(action === "suspend" ? "Company suspended" : "Company reactivated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveBranding = async () => {
    try {
      await api.put(`/platform/companies/${id}/branding`, branding);
      toast.success("Branding saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addMember = async () => {
    if (!newMember.email) return;
    try {
      await api.post(`/platform/companies/${id}/members`, { email: newMember.email, tenant_roles: [newMember.role] });
      toast.success("Member added");
      setNewMember({ email: "", role: "operations" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateMemberRole = async (uid, role) => {
    try {
      await api.patch(`/platform/companies/${id}/members/${uid}`, { tenant_roles: [role] });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member's access to this company?")) return;
    try {
      await api.delete(`/platform/companies/${id}/members/${uid}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const startSupport = async () => {
    if (!support.reason.trim()) return toast.error("A reason is required");
    try {
      const { data } = await api.post(`/platform/companies/${id}/support-session`, support);
      localStorage.setItem("operix_support_session", data.id);
      localStorage.setItem("operix_support_company", id);
      toast.success("Support session started");
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const roles = meta.roles || [];

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="platform-company-detail">
      <button onClick={() => navigate("/platform/companies")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 mb-4" data-testid="company-back">
        <ArrowLeft className="w-4 h-4" /> All companies
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{company.name}</h1>
          <div className="text-sm text-slate-500">/{company.slug} · created {(company.created_at || "").slice(0, 10)}</div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${company.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="company-status-pill">{company.status}</span>
      </div>

      <Section title="Details & plan" icon={Save} testId="company-details-section">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="company-name-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Plan</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className={`${inputCls} capitalize`} data-testid="company-plan-select">
              {meta.plans.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <button onClick={() => saveCompany()} className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="save-company-button">Save changes</button>
      </Section>

      <Section title="Module entitlement" testId="company-modules-section">
        <p className="text-xs text-slate-500 mb-3">Only Platform Admin can change entitlement. Disabled modules are blocked server-side, not just hidden.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {meta.all_modules.map((m) => (
            <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm capitalize ${modules.includes(m) ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/30" : "bg-white/5 text-slate-400"}`} data-testid={`module-toggle-${m}`}>
              <input type="checkbox" checked={modules.includes(m)} onChange={() => toggleModule(m)} className="accent-emerald-500" />
              {m}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => saveCompany()} className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="save-modules-button">Save modules</button>
          <button onClick={applyPlanBundle} className="rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm px-4 py-2" data-testid="apply-plan-bundle">Reset to plan bundle</button>
        </div>
      </Section>

      <Section title="Branding" icon={Palette} testId="company-branding-section">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Display name</label>
            <input value={branding.display_name || ""} onChange={(e) => setBranding((b) => ({ ...b, display_name: e.target.value }))} className={inputCls} data-testid="branding-display-name" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Logo URL</label>
            <input value={branding.logo_url || ""} onChange={(e) => setBranding((b) => ({ ...b, logo_url: e.target.value }))} className={inputCls} data-testid="branding-logo" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Primary colour</label>
            <input value={branding.primary_color || ""} onChange={(e) => setBranding((b) => ({ ...b, primary_color: e.target.value }))} className={inputCls} placeholder="#4f46e5" data-testid="branding-primary" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Accent colour</label>
            <input value={branding.accent_color || ""} onChange={(e) => setBranding((b) => ({ ...b, accent_color: e.target.value }))} className={inputCls} placeholder="#22d3ee" data-testid="branding-accent" />
          </div>
        </div>
        <button onClick={saveBranding} className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="save-branding-button">Save branding</button>
      </Section>

      <Section title={`Members (${members.length})`} icon={Users} testId="company-members-section">
        <div className="space-y-2 mb-4">
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2" data-testid={`member-row-${m.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                <div className="text-xs text-slate-500 truncate">{m.email}{m.platform_role ? " · platform admin" : ""}</div>
              </div>
              <select value={m.tenant_roles[0] || ""} onChange={(e) => updateMemberRole(m.user_id, e.target.value)} className={`${inputCls} w-40`} data-testid={`member-role-${m.user_id}`}>
                {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select>
              <button onClick={() => removeMember(m.user_id)} className="p-2 rounded-lg text-rose-400 hover:bg-rose-500/10" data-testid={`remove-member-${m.user_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={newMember.email} onChange={(e) => setNewMember((n) => ({ ...n, email: e.target.value }))} placeholder="email@company.com" className={`${inputCls} flex-1`} data-testid="new-member-email" />
          <select value={newMember.role} onChange={(e) => setNewMember((n) => ({ ...n, role: e.target.value }))} className={`${inputCls} sm:w-44`} data-testid="new-member-role">
            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
          <button onClick={addMember} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="add-member-button">
            <UserPlus className="w-4 h-4" /> Add / invite
          </button>
        </div>
      </Section>

      <Section title="Support access" icon={LifeBuoy} testId="company-support-section">
        <p className="text-xs text-slate-500 mb-3">Enter this workspace acting-as a role. Time-boxed and fully audited. Read-only by default; enable write only when necessary.</p>
        {company.status !== "active" ? (
          <div className="text-sm text-amber-300">Reactivate the company to start a support session.</div>
        ) : (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Acting as role</label>
                <select value={support.acting_role} onChange={(e) => setSupport((s) => ({ ...s, acting_role: e.target.value }))} className={inputCls} data-testid="support-role">
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Mode</label>
                <select value={support.mode} onChange={(e) => setSupport((s) => ({ ...s, mode: e.target.value }))} className={inputCls} data-testid="support-mode">
                  <option value="read">Read-only</option>
                  <option value="write">Read &amp; write</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Duration (min)</label>
                <input type="number" value={support.minutes} min={1} max={240} onChange={(e) => setSupport((s) => ({ ...s, minutes: Number(e.target.value) }))} className={inputCls} data-testid="support-minutes" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={support.reason} onChange={(e) => setSupport((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason (required)" className={inputCls} data-testid="support-reason" />
              <input value={support.ticket_ref} onChange={(e) => setSupport((s) => ({ ...s, ticket_ref: e.target.value }))} placeholder="Ticket reference (optional)" className={inputCls} data-testid="support-ticket" />
            </div>
            <button onClick={startSupport} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="start-support-button">
              <LifeBuoy className="w-4 h-4" /> Start support session
            </button>
          </div>
        )}
      </Section>

      <Section title="Lifecycle" testId="company-lifecycle-section">
        {company.status === "active" ? (
          <button onClick={() => window.confirm("Suspend this company? Members lose access; data is preserved.") && setStatus("suspend")} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="suspend-company-button">
            <PowerOff className="w-4 h-4" /> Suspend company
          </button>
        ) : (
          <button onClick={() => setStatus("activate")} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-4 py-2" data-testid="activate-company-button">
            <Power className="w-4 h-4" /> Reactivate company
          </button>
        )}
        <p className="text-xs text-slate-500 mt-2">Suspension is reversible and non-destructive — no data, members or configuration are deleted.</p>
      </Section>
    </div>
  );
}
